import { useState, useEffect, useCallback, Children, isValidElement } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { codeToHtml } from "shiki";
import { Button } from "./ui/button";
import { CodeEditor } from "./CodeEditor";
import { ChevronLeft, ChevronRight, ExternalLink, BookOpen, Target, Library, CheckCircle2 } from "lucide-react";
import API_BASE_URL from "../config";
import "./LessonContent.css";

// Функция для парсинга markdown ссылок в массив объектов {title, url}
const parseMarkdownLinks = (markdown) => {
  if (!markdown) return [];
  
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const resources = [];
  let match;
  
  while ((match = linkRegex.exec(markdown)) !== null) {
    resources.push({
      title: match[1],
      url: match[2]
    });
  }
  
  return resources;
};

/** codeblock модуля 5 в markdown с fence solidity (только отображение, как остальной контент) */
function wrapSolidityCodeblockMarkdown(code) {
  if (!code) return "";
  let normalized = code.replace(/\r\n/g, "\n");
  let fence = "```";
  while (normalized.includes(fence)) {
    fence += "`";
    if (fence.length > 12) break;
  }
  return `${fence}solidity\n${normalized}\n${fence}`;
}

const SHIKI_THEME = "github-dark";

const SHIKI_LANG_ALIASES = {
  js: "javascript",
  sol: "solidity",
  sh: "bash",
  shell: "bash",
  zsh: "bash",
  yml: "yaml",
  md: "markdown",
};

function normalizeShikiLang(raw) {
  const key = (raw || "").toLowerCase().trim();
  return SHIKI_LANG_ALIASES[key] || key;
}

function classNameToString(className) {
  if (typeof className === "string") return className;
  if (Array.isArray(className)) {
    return className.flat().filter(Boolean).join(" ");
  }
  return "";
}

function MarkdownPre({ children }) {
  const parts = Children.toArray(children);
  const codeEl = parts.find(
    (c) => isValidElement(c) && c.type === "code"
  );
  const cls = codeEl ? classNameToString(codeEl.props.className) : "";
  const match = cls ? /language-(\S+)/.exec(cls) : null;

  if (codeEl && match) {
    const code = String(codeEl.props.children ?? "").replace(/\n$/, "");
    return (
      <ShikiCodeBlock lang={normalizeShikiLang(match[1])} code={code} />
    );
  }

  return <pre>{children}</pre>;
}

function ShikiCodeBlock({ lang, code }) {
  const [html, setHtml] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const out = await codeToHtml(code, {
          lang,
          theme: SHIKI_THEME,
        });
        if (!cancelled) setHtml(out);
      } catch {
        try {
          const fallback = await codeToHtml(code, {
            lang: "text",
            theme: SHIKI_THEME,
          });
          if (!cancelled) setHtml(fallback);
        } catch {
          if (!cancelled) setHtml(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lang, code]);

  if (html) {
    return (
      <div
        className="markdown-shiki-block"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  return (
    <pre>
      <code>{code}</code>
    </pre>
  );
}

// Компонент для рендеринга markdown контента
const MarkdownContent = ({ content }) => {
  if (!content) return null;

  return (
    <div className="markdown-content">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          pre: MarkdownPre,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

const introPlaceholder = {
  title: "Описание самоучителя",
  overview: `## Модуль 1: Старт в Web3. От нуля до своего первого смарт-контракта

Превратитесь из начинающего в разработчика за 54 урока.

Вы начнете с самых основ: что такое код, алгоритмы и почему блокчейн меняет правила игры. Мы проведем вас через все слои Web3 — от концепции децентрализации до архитектуры Ethereum, виртуальной машины (EVM) и механизмов консенсуса. Но главное — вы сразу начнете практиковаться. Вы освоите Solidity, главный язык смарт-контрактов, от базовых типов данных и переменных до сложных структур, функций и модификаторов. К концу модуля вы не просто будете понимать теорию — вы самостоятельно напишете и развернете свой первый набор полноценных смарт-контрактов, готовых к взаимодействию с блокчейном.

### Вы научитесь:

- Понимать фундаментальные принципы блокчейна, Web3 и сети Ethereum.
- Свободно ориентироваться в ключевых концепциях: транзакции, аккаунты, газ, состояние сети.
- Писать на Solidity: работать со всеми типами данных, функциями, событиями, ошибками и циклами.
- Строить сложную логику с помощью наследования, интерфейсов и библиотек (включая OpenZeppelin).
- Понимать, что происходит "под капотом" на уровне EVM при вызове контракта.
- **Итог:** Научитесь создавать базовые смарт контракты и решите финальный практикум, закрепляющий все навыки модуля.

## Модуль 2: Инструменты мастера. Стандарты, тесты и реальные паттерны

Перестаньте писать учебные примеры. Начните создавать готовые к продакшену продукты.

Этот модуль — ваш переход в профессиональную лигу. Вы настроите идеальное рабочее окружение (VSCode, Foundry, Hardhat, GitHub) и погрузитесь в промышленные стандарты — ERC-20, ERC-721, ERC-4626. Вы не просто изучите их спецификацию, а научитесь имплементировать, тестировать и безопасно интегрировать в свои проекты. Вы освоите два главных фреймворка для тестирования и деплоя, а затем примените эти знания, создавая реальные механизмы: голландские аукционы, мультисиг-кошельки, DAO и DeFi-паттерны. Это знания, за которые платят деньги.

### Вы научитесь:

- Профессионально работать с инструментами разработчика: Node.js, npm, Foundry, Hardhat, Git.
- Реализовывать, кастомизировать и аудировать стандарты токенов (ERC).
- Создавать и оптимизировать реальные системы: аукционы, управление, ролевой доступ, стейкинг.
- Понимать и предотвращать типичные уязвимости безопасности.
- **Итог:** Полноценный проект, объединяющий несколько стандартов и паттернов, который станет центральным элементом вашего портфолио.

## Модуль 3: Низкоуровневый код и максимальная оптимизация

Загляните "под капот" EVM и пишите код, который превосходит по эффективности и стоимости 99% других контрактов.

Когда стандартных инструментов недостаточно, наступает время ассемблера. Этот модуль для тех, кто хочет достичь мастерства. Вы разберете прокси-архитектуры (Transparent, UUPS, Diamond) для обновляемых контрактов, углубитесь в криптографию с ECDSA и поймете, как данные на самом деле хранятся и обрабатываются в EVM (storage, memory, calldata, stack). Вы будете напрямую работать с памятью, оптимизировать газ через битовые операции и Yul/Assembly, и научитесь отлаживать самые сложные случаи. Это знание отличает middle-разработчика от джуниора.

### Вы научитесь:

- Реализовывать и обновлять сложные системы с помощью паттернов прокси.
- Работать с криптографическими подписями внутри смарт-контрактов.
- Детально управлять памятью EVM для радикальной оптимизации затрат на газ.
- Писать и читать низкоуровневый код на Yul и ассемблере.
- Проводить низкоуровневую отладку транзакций.
- **Итог:** Способность писать высокоэффективные, минимальные по газу и сложные контракты, решающие уникальные задачи.

## Модуль 4: Инженер качества. Профессиональное тестирование и безупречный деплой

Добейтесь, чтобы ваш код работал безупречно в любой сети, при любых условиях.

Написать контракт — полдела. Убедиться, что он никогда не упадет и не будет взломан, — искусство. Вы станете гуру тестирования в Foundry. Вы освоите не только юнит-тесты, но и мощные техники: фаззинг, форк-тесты (работа с "живой" сетью), мутационное тестирование. Вы научитесь писать скрипты для деплоя в разные среды, безопасно управлять ключами и интегрироваться с внешним инструментарием. Этот модуль — ваша страховка от багов, хакерских атак и дорогостоящих ошибок на продакшене.

### Вы научитесь:

- Виртуозно владеть командной строкой Foundry (Cast, Anvil, Chisel).
- Проектировать и писать исчерпывающие наборы тестов всех видов.
- Тестировать контракты в условиях, идентичных mainnet (через форки).
- Создавать безопасные пайплайны деплоя в тестовые и основные сети.
- Внедрять современные методологии и программы тестирования.
- **Итог:** Уверенность в том, что ваш код выдержит любую атаку, и готовый набор скриптов/тестов для любого вашего проекта.

## Модуль 5: Практикум Аудитора. 71 челлендж по поиску уязвимостей

Натренируйте "взгляд взломщика", чтобы строить неприступные системы.

Безопасность — самый ценный навык в Web3. Этот модуль — уникальная возможность отточить его на практике. 71 реальный кейс, смоделированный на основе инцидентов из жизни. Вы будете искать, эксплуатировать и фиксить уязвимости: от классических reentrancy и overflow до тонких логических ошибок в сложных DeFi-протоколах. Это симуляция реальных bug bounty программ и аудиторских проверок. После этого модуля вы сможете не только писать безопасный код, но и профессионально оценивать чужой.

### Вы научитесь:

- Систематически анализировать код на наличие уязвимостей.
- Понимать мотивацию и методы атакующих.
- Применять на практике все принципы безопасной разработки.
- Мыслить как пентестер и независимый аудитор.
- **Итог:** Опыт, эквивалентный участию в десятках bug bounty программ, и решающее преимущество на рынке труда как для разработчика, так и для аудитора.`,
  steps: [
    "Выберите модуль в боковом меню и откройте урок.",
    "Изучите материалы, выполните задания и отметьте урок выполненным.",
    "Следите за прогрессом и открывайте новые модули по мере продвижения."
  ]
};

const introStats = [
  { label: "Уроков", value: "200+" },
  { label: "Вопросов для самопроверки", value: "100+" },
  { label: "Челленджей", value: "50+" },
  { label: "Практика в каждом уроке", value: "Да" },
  { label: "Практических заданий", value: "100+" }
];

const moduleHighlights = [
  {
    title: "Модуль 1 — Старт",
    text: "Web3/Ethereum, базовый Solidity, первый контракт."
  },
  {
    title: "Модуль 2 — Инструменты",
    text: "Foundry (база), Hardhat (база), стандарты ERC, тесты, аукционы, DAO."
  },
  {
    title: "Модуль 3 — Оптимизация",
    text: "EVM, storage/memory, Yul/Assembly, прокси-паттерны."
  },
  {
    title: "Модуль 4 — Качество",
    text: "Фаззинг, форк-тесты, мутационное тестирование, деплой."
  },
  {
    title: "Модуль 5 — Аудит",
    text: "71 челлендж, reentrancy, overflow, DeFi-логика."
  }
];

const valueProps = [
  {
    title: "Портфолио и практика",
    text: "Готовые контракты, финальный практикум, задания в каждом уроке."
  },
  {
    title: "Боевые навыки",
    text: "Инструменты, тесты, деплой, безопасность и аудит на реальных паттернах."
  },
  {
    title: "Рост до продакшена",
    text: "Стандарты ERC, оптимизация газа, прокси-архитектуры и работа с EVM."
  }
];

// Данные для страниц содержания модулей
const moduleContentsData = {
  1: {
    title: "Модуль 1: Старт в Web3",
    subtitle: "От нуля до своего первого смарт-контракта",
    description: "Превратитесь из новичка в разработчика за 54 урока. Вы начнете с самых основ: что такое код, алгоритмы и почему блокчейн меняет правила игры.",
    badge: "Базовый уровень",
    gradient: "linear-gradient(135deg, rgba(76, 29, 149, 0.28), rgba(14, 165, 233, 0.24))",
    buttonStyle: {
      background: "linear-gradient(135deg, #7c3aed, #0ea5e9)",
      color: "#ffffff",
      border: "1px solid rgba(255,255,255,0.2)"
    },
    stats: [
      { label: "Уроков", value: "54" },
      { label: "Практических заданий", value: "30+" },
      { label: "Уровень", value: "Start" },
      { label: "Время прохождения", value: "~100 часов" }
    ],
    highlights: [
      {
        title: "Основы блокчейна и Web3",
        text: "Концепции децентрализации, архитектура Ethereum, EVM и механизмы консенсуса."
      },
      {
        title: "Язык Solidity с нуля",
        text: "Типы данных, переменные, функции, модификаторы, события и обработка ошибок."
      },
      {
        title: "Продвинутые конструкции",
        text: "Наследование, интерфейсы, библиотеки OpenZeppelin и паттерны разработки."
      },
      {
        title: "Первые контракты",
        text: "Создание и развертывание полноценных смарт-контрактов в блокчейн."
      }
    ],
    valueProps: [
      {
        title: "Полное понимание основ",
        text: "Фундаментальные принципы блокчейна, Ethereum и Web3-экосистемы."
      },
      {
        title: "Владение Solidity",
        text: "Уверенное программирование на главном языке смарт-контрактов."
      },
      {
        title: "Практическое портфолио",
        text: "Набор рабочих контрактов и решенный финальный практикум."
      }
    ]
  },
  2: {
    title: "Модуль 2: Инструменты мастера",
    subtitle: "Стандарты, тесты и реальные паттерны",
    description: "Перестаньте писать учебные примеры. Начните создавать готовые к продакшену продукты с профессиональными инструментами и стандартами.",
    badge: "Средний уровень",
    gradient: "linear-gradient(135deg, rgba(59, 130, 246, 0.28), rgba(16, 185, 129, 0.24))",
    buttonStyle: {
      background: "linear-gradient(135deg, #3b82f6, #10b981)",
      color: "#ffffff",
      border: "1px solid rgba(255,255,255,0.2)"
    },
    stats: [
      { label: "Уроков", value: "40" },
      { label: "Стандартов ERC", value: "3+" },
      { label: "Уровень", value: "Mid" },
      { label: "Время прохождения", value: "~150 часов" }
    ],
    highlights: [
      {
        title: "Профессиональные инструменты",
        text: "VSCode, Foundry, Hardhat, GitHub - полная настройка рабочего окружения."
      },
      {
        title: "Стандарты токенов ERC",
        text: "ERC-20, ERC-721, ERC-4626 - имплементация, тестирование, интеграция."
      },
      {
        title: "Комплексное тестирование",
        text: "Написание надежных тестов для смарт-контрактов в Foundry и Hardhat."
      },
      {
        title: "Реальные DeFi-паттерны",
        text: "Аукционы, мультисиг, DAO, стейкинг - механизмы, за которые платят деньги."
      }
    ],
    valueProps: [
      {
        title: "Продакшен-навыки",
        text: "Работа с профессиональными фреймворками и инструментами разработки."
      },
      {
        title: "Мастерство стандартов",
        text: "Реализация и аудит популярных стандартов токенов и протоколов."
      },
      {
        title: "Портфолио-проект",
        text: "Полноценный проект с несколькими стандартами - центральный элемент резюме."
      }
    ]
  },
  3: {
    title: "Модуль 3: Сверхспособности разработчика",
    subtitle: "Низкоуровневый код и максимальная оптимизация",
    description: "Загляните под капот EVM и пишите код, который превосходит по эффективности и стоимости 90% других контрактов.",
    badge: "Продвинутый уровень",
    gradient: "linear-gradient(135deg, rgba(168, 85, 247, 0.28), rgba(236, 72, 153, 0.24))",
    buttonStyle: {
      background: "linear-gradient(135deg, #a855f7, #ec4899)",
      color: "#ffffff",
      border: "1px solid rgba(255,255,255,0.2)"
    },
    stats: [
      { label: "Уроков", value: "18" },
      { label: "Паттернов прокси", value: "3+" },
      { label: "Уровень", value: "Adv" },
      { label: "Время прохождения", value: "~50 часов" }
    ],
    highlights: [
      {
        title: "Прокси-архитектуры",
        text: "Transparent, UUPS, Diamond - апгрейдable контракты для сложных систем."
      },
      {
        title: "Криптография в контрактах",
        text: "ECDSA подписи, верификация и безопасная работа с криптографией."
      },
      {
        title: "Внутренности EVM",
        text: "Storage, memory, calldata, stack - детальное управление памятью."
      },
      {
        title: "Yul и Assembly",
        text: "Низкоуровневая оптимизация газа через ассемблер и битовые операции."
      }
    ],
    valueProps: [
      {
        title: "Senior-разработчик",
        text: "Знания, отличающие мастера от джуна в индустрии блокчейна."
      },
      {
        title: "Экстремальная оптимизация",
        text: "Радикальное снижение затрат на газ и максимальная эффективность кода."
      },
      {
        title: "Сложные системы",
        text: "Способность создавать уникальные решения для нестандартных задач."
      }
    ]
  },
  4: {
    title: "Модуль 4: Инженер качества",
    subtitle: "Профессиональное тестирование и безупречный деплой",
    description: "Добейтесь, чтобы ваш код работал безупречно в любой сети, при любых условиях. Мастерство тестирования - ваша страховка от багов и атак.",
    badge: "Профессиональный уровень",
    gradient: "linear-gradient(135deg, rgba(34, 197, 94, 0.28), rgba(59, 130, 246, 0.24))",
    buttonStyle: {
      background: "linear-gradient(135deg, rgb(17 101 48), rgb(37 56 79))",
      color: "#ffffff",
      border: "1px solid rgba(255,255,255,0.2)"
    },
    stats: [
      { label: "Уроков", value: "20" },
      { label: "Видов тестирования", value: "5+" },
      { label: "Уровень", value: "Pro" },
      { label: "Время прохождения", value: "~40 часов" }
    ],
    highlights: [
      {
        title: "Foundry как оружие",
        text: "Виртуозное владение Cast, Anvil, Chisel и всей экосистемой Foundry."
      },
      {
        title: "Продвинутое тестирование",
        text: "Фаззинг, форк-тесты, мутационное тестирование - находим невидимые баги."
      },
      {
        title: "Тесты на mainnet",
        text: "Работа с живыми сетями через форки - условия идентичные продакшену."
      },
      {
        title: "Безопасный деплой",
        text: "Пайплайны развертывания, управление ключами, интеграция с инструментами."
      }
    ],
    valueProps: [
      {
        title: "Абсолютная уверенность",
        text: "Ваш код выдержит любую атаку благодаря исчерпывающим тестам."
      },
      {
        title: "Готовые решения",
        text: "Набор скриптов и тестов для быстрого старта любого проекта."
      },
      {
        title: "Методологии индустрии",
        text: "Современные практики тестирования и деплоя от лучших команд."
      }
    ]
  },
  5: {
    title: "Модуль 5: Практикум Аудитора",
    subtitle: "71 челлендж по поиску уязвимостей",
    description: "Натренируйте взгляд взломщика, чтобы строить неприступные системы. Безопасность - самый ценный навык в Web3.",
    badge: "Экспертный уровень",
    gradient: "linear-gradient(135deg, rgba(239, 68, 68, 0.28), rgba(249, 115, 22, 0.24))",
    buttonStyle: {
      background: "linear-gradient(135deg, rgb(105 49 49), rgb(109 55 18))",
      color: "#ffffff",
      border: "1px solid rgba(255,255,255,0.2)"
    },
    stats: [
      { label: "Челленджей", value: "71" },
      { label: "Типов уязвимостей", value: "30+" },
      { label: "Уровень", value: "Exp" },
      { label: "Время прохождения", value: "~50 часов" }
    ],
    highlights: [
      {
        title: "Реальные кейсы",
        text: "71 задача на основе реальных инцидентов из истории DeFi и Web3."
      },
      {
        title: "Классические уязвимости",
        text: "Reentrancy, overflow, underflow - поиск, эксплуатация и исправление."
      },
      {
        title: "Сложные DeFi-баги",
        text: "Логические ошибки в протоколах, манипуляции с ценой, флеш-лоаны."
      },
      {
        title: "Мышление аудитора",
        text: "Систематический анализ кода и понимание мотивации атакующих."
      }
    ],
    valueProps: [
      {
        title: "Bug Bounty навыки",
        text: "Опыт, эквивалентный участию в десятках программ поиска уязвимостей."
      },
      {
        title: "Безопасный код",
        text: "Способность писать контракты, защищенные от всех известных атак."
      },
      {
        title: "Карьера аудитора",
        text: "Решающее преимущество на рынке как разработчика и независимого аудитора."
      }
    ]
  }
};

const contactsPlaceholder = {
  title: "Контакты",
  overview: "Добро пожаловать в раздел поддержки! Здесь вы найдете все необходимые каналы связи для комфортного обучения и получения актуальной информации о курсе. Мы всегда на связи и готовы помочь вам в освоении Web3-разработки.",
  items: [
    "**Официальный Telegram-канал:** [Solidity. Смарт-контракты и аудит](https://t.me/solidityset) — здесь публикуется вся актуальная информация о покупке модулей, способах оплаты и условиях доступа к премиум-контенту.",
    "**Группа поддержки:** Возникли вопросы в процессе обучения? Присоединяйтесь к группе нашего Telegram-канала — администраторы и опытные участники сообщества всегда готовы помочь разобраться с материалом и ответить на ваши вопросы.",
    "**Новости и обновления курса:** Следите за анонсами новых уроков, улучшениями материалов и важными новостями проекта в официальном канале."
  ]
};

export const LessonContent = ({ module, lessonId }) => {
  const navigate = useNavigate();
  const [lessonData, setLessonData] = useState(null);
  const [moduleContents, setModuleContents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [moduleLessons, setModuleLessons] = useState([]);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(-1);
  const [substat, setSubstat] = useState(0);
  const [spoilersOpen, setSpoilersOpen] = useState({ hint: false, answer: false });

  // Проверка авторизации и получение substat
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/me`, {
          credentials: 'include' // Отправляем cookies
        });
        
        if (response.ok) {
          const data = await response.json();
          setIsAuthenticated(data.authenticated || true);
          setSubstat(data.substat || 0);
        } else {
          setIsAuthenticated(false);
          setSubstat(0);
        }
      } catch (err) {
        console.error("Ошибка получения информации о пользователе:", err);
        setIsAuthenticated(false);
        setSubstat(0);
      }
    };

    checkAuth();
  }, []);

  // Применение логики блокировки уроков на основе авторизации и substat
  const applyAccessControl = useCallback((lessons, moduleNum) => {
    if (moduleNum === 6) {
      if (!isAuthenticated) {
        return lessons.map((lesson) => ({ ...lesson, locked: true }));
      }
      if (substat >= 1) {
        return lessons.map((lesson) => ({ ...lesson, locked: false }));
      }
      return lessons.map((lesson) => ({ ...lesson, locked: true }));
    }
    if (!isAuthenticated) {
      // Если не зарегистрирован - только первые 3 урока в Module 1 открыты
      if (moduleNum === 1) {
        return lessons.map((lesson, index) => ({
          ...lesson,
          locked: index >= 3
        }));
      }
      // Для остальных модулей все закрыты
      return lessons.map(lesson => ({
        ...lesson,
        locked: true
      }));
    } else {
      // Определяем, какие модули должны быть открыты на основе substat
      const getOpenModules = (substatValue) => {
        switch (substatValue) {
          case 0:
            // Первые 3 урока в Module 1 открыты
            return { module_1: { all: false, firstThree: true } };
          case 1:
            // Все уроки Module 1 открыты
            return { module_1: { all: true } };
          case 2:
            // Все уроки Module 2 открыты
            return { module_2: { all: true } };
          case 3:
            // Все уроки Module 3 открыты
            return { module_3: { all: true } };
          case 4:
            // Все уроки Module 4 открыты
            return { module_4: { all: true } };
          case 5:
            // Все уроки Module 5 открыты
            return { module_5: { all: true } };
          case 6:
            // Все уроки Module 1 и Module 2 открыты
            return { module_1: { all: true }, module_2: { all: true } };
          case 7:
            // Все уроки Module 1, Module 2, Module 3 открыты
            return { module_1: { all: true }, module_2: { all: true }, module_3: { all: true } };
          case 8:
            // Все уроки Module 1, Module 2, Module 3, Module 4 открыты
            return { module_1: { all: true }, module_2: { all: true }, module_3: { all: true }, module_4: { all: true } };
          case 9:
            // Все уроки Module 1, Module 2, Module 3, Module 4, Module 5 открыты
            return { module_1: { all: true }, module_2: { all: true }, module_3: { all: true }, module_4: { all: true }, module_5: { all: true } };
          default:
            // По умолчанию - первые 3 урока в Module 1 открыты
            return { module_1: { all: false, firstThree: true } };
        }
      };

      const openModules = getOpenModules(substat);
      const moduleAccess = openModules[`module_${moduleNum}`];

      if (!moduleAccess) {
        // Модуль не открыт - все уроки закрыты
        return lessons.map(lesson => ({
          ...lesson,
          locked: true
        }));
      }

      if (moduleAccess.all) {
        // Все уроки модуля открыты
        return lessons.map(lesson => ({
          ...lesson,
          locked: false
        }));
      }

      if (moduleAccess.firstThree) {
        // Только первые 3 урока открыты
        return lessons.map((lesson, index) => ({
          ...lesson,
          locked: index >= 3
        }));
      }

      // По умолчанию все закрыты
      return lessons.map(lesson => ({
        ...lesson,
        locked: true
      }));
    }
  }, [isAuthenticated, substat]);

  // Загрузка структуры модуля для навигации и проверка доступа
  useEffect(() => {
    const fetchModuleStructure = async () => {
      if (!module || lessonId === "contents") return;

      try {
        const response = await fetch(`${API_BASE_URL}/api/lessons/modules`, {
          credentials: 'include' // Отправляем cookies
        });
        if (!response.ok) return;

        const data = await response.json();
        const moduleNum = parseInt(module);
        const moduleData = data.modules.find(m => m.id === `module_${moduleNum}`);
        
        if (moduleData) {
          // Для модулей 1-4 и 6 сортируем по lesson как по числу (10, 10.1, 10.2 и т.д.).
          const lessonsWithAccess = applyAccessControl(moduleData.lessons || [], moduleNum);
          const sortedLessons = moduleNum === 5
            ? lessonsWithAccess
            : [...lessonsWithAccess].sort((a, b) => {
                const numA = parseFloat(String(a.lesson ?? "").replace(",", ".")) || 0;
                const numB = parseFloat(String(b.lesson ?? "").replace(",", ".")) || 0;
                if (numA !== numB) return numA - numB;
                return (Number(a.id) || 0) - (Number(b.id) || 0);
              });

          setModuleLessons(sortedLessons);
          const index = sortedLessons.findIndex(l => String(l.id) === String(lessonId));
          
          // Проверяем, заблокирован ли текущий урок
          if (index >= 0) {
            const currentLesson = sortedLessons[index];
            if (currentLesson && currentLesson.locked) {
              // Если урок заблокирован - редирект на главную страницу
              navigate("/");
              return;
            }
            setCurrentLessonIndex(index);
          } else {
            // Если урок не найден - редирект на главную страницу
            navigate("/");
            return;
          }
        }
      } catch (err) {
        console.error("Ошибка загрузки структуры модуля:", err);
      }
    };

    fetchModuleStructure();
  }, [module, lessonId, isAuthenticated, substat, navigate, applyAccessControl]);

  // Проверка выполнения урока
  useEffect(() => {
    const checkCompleted = async () => {
      if (!isAuthenticated || !lessonId || lessonId === "contents") {
        setIsCompleted(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/finished/`, {
          credentials: 'include' // Отправляем cookies
        });

        if (response.ok) {
          const data = await response.json();
          const lessonIdNum = parseInt(lessonId);
          setIsCompleted(data.finishedLessons?.includes(lessonIdNum) || false);
        }
      } catch (err) {
        console.error("Ошибка проверки выполнения урока:", err);
      }
    };

    checkCompleted();
  }, [isAuthenticated, lessonId]);

  useEffect(() => {
    const fetchLessonData = async () => {
      if (!module && !lessonId) {
        setLessonData(null);
        setModuleContents(null);
        setError(null);
        setLoading(false);
        return;
      }

      if (module === "contacts" || lessonId === "contacts") {
        setLessonData(null);
        setModuleContents(null);
        setError(null);
        setLoading(false);
        return;
      }

      if (!module || !lessonId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const moduleNum = parseInt(module);

        if (moduleNum === 6 && lessonId === "contents") {
          navigate("/", { replace: true });
          return;
        }
        
        // Если lessonId === "contents", загружаем содержание модуля
        if (lessonId === "contents") {
          const url = `${API_BASE_URL}/api/lessons/module/${moduleNum}/contents`;
          const response = await fetch(url);
          
          if (!response.ok) {
            throw new Error(`Ошибка загрузки содержания модуля: ${response.statusText}`);
          }

          const data = await response.json();
          setModuleContents(data.lessons || []);
          setLessonData(null);
        } else {
          // Обычная загрузка урока
          let url;
          
          if ((moduleNum >= 1 && moduleNum <= 4) || moduleNum === 6) {
            // Модули 1-4 и 6: загружаем из таблицы lessons
            url = `${API_BASE_URL}/api/lessons/${lessonId}`;
          } else if (moduleNum === 5) {
            // Модуль 5: загружаем из таблицы bugs
            url = `${API_BASE_URL}/api/bugs/${lessonId}`;
          } else {
            throw new Error("Неверный номер модуля");
          }

          const response = await fetch(url);
          
          if (!response.ok) {
            throw new Error(`Ошибка загрузки урока: ${response.statusText}`);
          }

          const data = await response.json();
          setLessonData(data);
          setModuleContents(null);
        }
      } catch (err) {
        console.error("Ошибка загрузки:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLessonData();
  }, [module, lessonId, navigate]);

  useEffect(() => {
    setSpoilersOpen({ hint: false, answer: false });
  }, [lessonId]);

  if (loading) {
    return (
      <div className="lesson-content">
        <div className="lesson-content-inner">
          <div style={{ padding: "2rem", textAlign: "center", color: "#858585" }}>
            Загрузка урока...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lesson-content">
        <div className="lesson-content-inner">
          <div style={{ padding: "2rem", textAlign: "center", color: "#f87171" }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (module === "contacts" || lessonId === "contacts") {
    return (
      <div className="lesson-content">
        <div className="lesson-content-inner">
          <div className="lesson-header">
            <h1 className="lesson-title">{contactsPlaceholder.title}</h1>
            <div className="lesson-divider" />
          </div>

          <section className="lesson-section">
            <div className="section-header">
              <BookOpen className="section-icon section-icon-primary" />
              <h2 className="section-title">Overview</h2>
            </div>
            <div className="lesson-content-text">
              <MarkdownContent content={contactsPlaceholder.overview} />
            </div>
          </section>

          <section className="lesson-section">
            <div className="section-header">
              <Target className="section-icon section-icon-primary" />
              <h2 className="section-title">Телеграм канал</h2>
            </div>
            <div className="assignment-box">
              <div className="assignment-text">
                <MarkdownContent content={contactsPlaceholder.items.map((item, idx) => `${idx + 1}. ${item}`).join("\n")} />
              </div>
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (!module && !lessonId) {
    return (
      <div className="lesson-content">
        <div className="lesson-content-inner">
          <div className="lesson-header">
            <h1 className="lesson-title">{introPlaceholder.title}</h1>
            <div className="lesson-divider" />
          </div>

          {/* Hero */}
          <section className="lesson-section" style={{ padding: "1.25rem", borderRadius: "14px", background: "linear-gradient(135deg, rgba(76, 29, 149, 0.28), rgba(14, 165, 233, 0.24))", border: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 15px 35px rgba(0,0,0,0.25)" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ flex: "1 1 320px", minWidth: 280 }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", padding: "0.4rem 0.75rem", borderRadius: "999px", background: "rgba(255,255,255,0.08)", color: "#E0F2FE", fontSize: "0.9rem", marginBottom: "0.6rem" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#22d3ee" }} />
                  Web3, Solidity и аудит — от нуля до продакшена
                </div>
                <h2 style={{ color: "#F8FAFC", fontSize: "1.75rem", fontWeight: 800, lineHeight: 1.2, marginBottom: "0.6rem" }}>
                  Практика в каждом уроке: пишем, тестируем и улучшаем смарт-контракты
                </h2>
                <p style={{ color: "#E2E8F0", fontSize: "1rem", lineHeight: 1.6, maxWidth: 760 }}>
                  5 модулей: от базового Solidity до оптимизации контрактов и аудита.
                  Делайте задания, закрывайте челленджи, собирайте портфолио боевых контрактов.
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.6rem", marginTop: "0.9rem" }}>
                  <Button
                    onClick={() => {
                      if (isAuthenticated) {
                        navigate("/1/contents");
                      } else {
                        navigate("/auth?tab=signup");
                      }
                    }}
                  >
                    Начать с модуля 1
                  </Button>
                </div>
              </div>
              <div style={{ flex: "0 1 260px", minWidth: 240, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "0.6rem" }}>
                {introStats.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      borderRadius: "12px",
                      padding: "0.9rem 0.85rem",
                      background: "rgba(255,255,255,0.08)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      backdropFilter: "blur(10px)"
                    }}
                  >
                    <div style={{ color: "#E0F2FE", fontSize: "0.85rem" }}>{item.label}</div>
                    <div style={{ color: "#F8FAFC", fontSize: "1.3rem", fontWeight: 800 }}>{item.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Timeline of modules */}
          <section className="lesson-section">
            <div className="section-header">
              <BookOpen className="section-icon section-icon-primary" />
              <h2 className="section-title">Что внутри модулей</h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: "0.85rem" }}>
              {moduleHighlights.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    borderRadius: "14px",
                    padding: "1rem 1.05rem",
                    background: "linear-gradient(135deg, rgba(59,130,246,0.08), rgba(45,212,191,0.04))",
                    border: "1px solid rgba(255,255,255,0.08)",
                    position: "relative",
                    overflow: "hidden"
                  }}
                >
                  <div style={{ position: "absolute", top: 10, right: 12, color: "rgba(255,255,255,0.2)", fontWeight: 800 }}>{idx + 1}</div>
                  <div style={{ color: "#F8FAFC", fontSize: "1.05rem", fontWeight: 700, marginBottom: "0.35rem" }}>{item.title}</div>
                  <div style={{ color: "#CBD5E1", lineHeight: 1.45 }}>{item.text}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="lesson-section">
            <div className="section-header">
              <BookOpen className="section-icon section-icon-primary" />
              <h2 className="section-title">Overview</h2>
            </div>
            <div className="lesson-content-text">
              <MarkdownContent content={introPlaceholder.overview} />
            </div>
          </section>

          {/* Value props */}
          <section className="lesson-section">
            <div className="section-header">
              <Library className="section-icon section-icon-primary" />
              <h2 className="section-title">Что получаете</h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "0.9rem" }}>
              {valueProps.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    border: "1px solid rgba(255,255,255,0.1)",
                    background: "rgba(255,255,255,0.03)",
                    borderRadius: "12px",
                    padding: "1rem 1.05rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.4rem"
                  }}
                >
                  <span style={{ color: "#F8FAFC", fontSize: "1.05rem", fontWeight: 700 }}>{item.title}</span>
                  <span style={{ color: "#CBD5E1", lineHeight: 1.45 }}>{item.text}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="lesson-section">
            <div className="section-header">
              <Target className="section-icon section-icon-primary" />
              <h2 className="section-title">Как начать</h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.75rem" }}>
              {introPlaceholder.steps.map((step, idx) => (
                <div
                  key={idx}
                  style={{
                    border: "1px solid rgba(255,255,255,0.1)",
                    background: "rgba(255,255,255,0.02)",
                    borderRadius: "10px",
                    padding: "0.95rem 1rem",
                    color: "#E5E7EB",
                    lineHeight: 1.45
                  }}
                >
                  <div style={{ color: "#9CA3AF", fontSize: "0.95rem", marginBottom: "0.25rem" }}>Шаг {idx + 1}</div>
                  <div style={{ fontWeight: 600, color: "#F8FAFC" }}>{step}</div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    );
  }

  // Рендер содержания модуля
  if (lessonId === "contents" && moduleContents) {
    const moduleNum = parseInt(module);
    const moduleData = moduleContentsData[moduleNum];

    if (!moduleData) {
      // Fallback на простой вид, если данных нет
      return (
        <div className="lesson-content">
          <div className="lesson-content-inner">
            <div className="lesson-header">
              <h1 className="lesson-title">Содержание модуля</h1>
              <div className="lesson-divider" />
            </div>

            <section className="lesson-section">
              <div className="lesson-content-text">
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {moduleContents.map((lesson, index) => (
                    <li key={lesson.id} style={{ padding: "0.5rem 0", fontSize: "1rem", color: "#e0e0e0" }}>
                      Урок {index + 1} - {lesson.lesson} - {lesson.title}
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          </div>
        </div>
      );
    }

    return (
      <div className="lesson-content">
        <div className="lesson-content-inner">
          <div className="lesson-header">
            <h1 className="lesson-title">{moduleData.title}</h1>
            <div className="lesson-divider" />
          </div>

          {/* Hero Section */}
          <section className="lesson-section" style={{ padding: "1.25rem", borderRadius: "14px", background: moduleData.gradient, border: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 15px 35px rgba(0,0,0,0.25)" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ flex: "1 1 320px", minWidth: 280 }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", padding: "0.4rem 0.75rem", borderRadius: "999px", background: "rgba(255,255,255,0.08)", color: "#E0F2FE", fontSize: "0.9rem", marginBottom: "0.6rem" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#22d3ee" }} />
                  {moduleData.badge}
                </div>
                <h2 style={{ color: "#F8FAFC", fontSize: "1.75rem", fontWeight: 800, lineHeight: 1.2, marginBottom: "0.6rem" }}>
                  {moduleData.subtitle}
                </h2>
                <p style={{ color: "#E2E8F0", fontSize: "1rem", lineHeight: 1.6, maxWidth: 760 }}>
                  {moduleData.description}
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.6rem", marginTop: "0.9rem" }}>
                  <Button
                    onClick={() => {
                      if (moduleContents.length > 0) {
                        const firstLesson = moduleContents[0];
                        navigate(`/${module}/${firstLesson.id}`);
                      }
                    }}
                    style={moduleData.buttonStyle}
                  >
                    Начать первый урок
                  </Button>
                </div>
              </div>
              <div style={{ flex: "0 1 260px", minWidth: 240, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "0.6rem" }}>
                {moduleData.stats.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      borderRadius: "12px",
                      padding: "0.9rem 0.85rem",
                      background: "rgba(255,255,255,0.08)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      backdropFilter: "blur(10px)"
                    }}
                  >
                    <div style={{ color: "#E0F2FE", fontSize: "0.85rem" }}>{item.label}</div>
                    <div style={{ color: "#F8FAFC", fontSize: "1.3rem", fontWeight: 800 }}>{item.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Key Topics */}
          <section className="lesson-section">
            <div className="section-header">
              <BookOpen className="section-icon section-icon-primary" />
              <h2 className="section-title">Ключевые темы модуля</h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "0.85rem" }}>
              {moduleData.highlights.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    borderRadius: "14px",
                    padding: "1rem 1.05rem",
                    background: "linear-gradient(135deg, rgba(59,130,246,0.08), rgba(45,212,191,0.04))",
                    border: "1px solid rgba(255,255,255,0.08)",
                    position: "relative",
                    overflow: "hidden"
                  }}
                >
                  <div style={{ position: "absolute", top: 10, right: 12, color: "rgba(255,255,255,0.2)", fontWeight: 800, fontSize: "1.5rem" }}>{idx + 1}</div>
                  <div style={{ color: "#F8FAFC", fontSize: "1.05rem", fontWeight: 700, marginBottom: "0.35rem" }}>{item.title}</div>
                  <div style={{ color: "#CBD5E1", lineHeight: 1.45, fontSize: "0.95rem" }}>{item.text}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Lessons List */}
          <section className="lesson-section">
            <div className="section-header">
              <Library className="section-icon section-icon-primary" />
              <h2 className="section-title">Все уроки модуля ({moduleContents.length})</h2>
            </div>
            <div style={{ display: "grid", gap: "0.6rem" }}>
              {moduleContents.map((lesson, index) => (
                <div
                  key={lesson.id}
                  onClick={() => navigate(`/${module}/${lesson.id}`)}
                  style={{
                    border: "1px solid rgba(255,255,255,0.1)",
                    background: "rgba(255,255,255,0.02)",
                    borderRadius: "10px",
                    padding: "0.9rem 1rem",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.02)";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
                  }}
                >
                  <div style={{ 
                    minWidth: "42px", 
                    width: "42px", 
                    height: "42px", 
                    borderRadius: "8px", 
                    background: "linear-gradient(135deg, rgba(59,130,246,0.2), rgba(147,51,234,0.2))",
                    border: "1px solid rgba(255,255,255,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    color: "#F8FAFC",
                    fontSize: "0.95rem"
                  }}>
                    {index + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: "#F8FAFC", fontSize: "1rem", fontWeight: 600, marginBottom: "0.15rem" }}>
                      {lesson.title}
                    </div>
                    <div style={{ color: "#9CA3AF", fontSize: "0.85rem" }}>
                      Урок {index + 1}
                    </div>
                  </div>
                  <ChevronRight style={{ width: "20px", height: "20px", color: "#9CA3AF" }} />
                </div>
              ))}
            </div>
          </section>

          {/* What You'll Get */}
          <section className="lesson-section">
            <div className="section-header">
              <Target className="section-icon section-icon-primary" />
              <h2 className="section-title">Что вы получите</h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "0.9rem" }}>
              {moduleData.valueProps.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    border: "1px solid rgba(255,255,255,0.1)",
                    background: "rgba(255,255,255,0.03)",
                    borderRadius: "12px",
                    padding: "1rem 1.05rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.4rem"
                  }}
                >
                  <span style={{ color: "#F8FAFC", fontSize: "1.05rem", fontWeight: 700 }}>{item.title}</span>
                  <span style={{ color: "#CBD5E1", lineHeight: 1.45 }}>{item.text}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Start Button */}
          <section className="lesson-section">
            <div style={{ 
              textAlign: "center", 
              padding: "1.5rem", 
              borderRadius: "14px",
              background: "linear-gradient(135deg, rgba(59,130,246,0.1), rgba(147,51,234,0.1))",
              border: "1px solid rgba(255,255,255,0.08)"
            }}>
              <h3 style={{ color: "#F8FAFC", fontSize: "1.25rem", fontWeight: 700, marginBottom: "0.75rem" }}>
                Готовы начать?
              </h3>
              <p style={{ color: "#CBD5E1", marginBottom: "1rem", fontSize: "0.95rem" }}>
                Переходите к первому уроку и начинайте изучение модуля
              </p>
              <Button
                onClick={() => {
                  if (moduleContents.length > 0) {
                    const firstLesson = moduleContents[0];
                    navigate(`/${module}/${firstLesson.id}`);
                  }
                }}
                style={{ ...moduleData.buttonStyle, fontSize: "1rem", padding: "0.6rem 1.5rem" }}
              >
                Начать обучение
              </Button>
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (!lessonData) {
    return (
      <div className="lesson-content">
        <div className="lesson-content-inner">
          <div style={{ padding: "2rem", textAlign: "center", color: "#f87171" }}>
            Урок не найден
          </div>
        </div>
      </div>
    );
  }

  // Функции для работы с выполненными уроками
  const handleFinishLesson = async () => {
    if (!isAuthenticated || !lessonId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/finished/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: 'include', // Отправляем cookies
        body: JSON.stringify({ lessonId: parseInt(lessonId) })
      });

      if (response.ok) {
        setIsCompleted(true);
        window.dispatchEvent(new CustomEvent("lessonCompletionChanged", {
          detail: {
            lessonId: parseInt(lessonId),
            completed: true
          }
        }));
      }
    } catch (err) {
      console.error("Ошибка отметки урока:", err);
    }
  };

  const handleUnfinishLesson = async () => {
    if (!isAuthenticated || !lessonId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/finished/${lessonId}`, {
        method: "DELETE",
        credentials: 'include' // Отправляем cookies
      });

      if (response.ok) {
        setIsCompleted(false);
        window.dispatchEvent(new CustomEvent("lessonCompletionChanged", {
          detail: {
            lessonId: parseInt(lessonId),
            completed: false
          }
        }));
      }
    } catch (err) {
      console.error("Ошибка снятия отметки урока:", err);
    }
  };

  // Навигация
  const handlePrevious = () => {
    if (currentLessonIndex > 0) {
      const prevLesson = moduleLessons[currentLessonIndex - 1];
      // Проверяем, не заблокирован ли предыдущий урок
      if (prevLesson && !prevLesson.locked) {
        navigate(`/${module}/${prevLesson.id}`);
      }
    }
  };

  const handleNext = () => {
    if (currentLessonIndex < moduleLessons.length - 1) {
      const nextLesson = moduleLessons[currentLessonIndex + 1];
      // Проверяем, не заблокирован ли следующий урок
      if (nextLesson && !nextLesson.locked) {
        navigate(`/${module}/${nextLesson.id}`);
      } else if (nextLesson && nextLesson.locked) {
        // Если урок заблокирован, отправляем событие для открытия модального окна
        window.dispatchEvent(new CustomEvent('openUnlockModal', { 
          detail: { lessonId: nextLesson.id, moduleNumber: parseInt(module) } 
        }));
      }
    }
  };

  // Проверяем доступность навигации с учетом блокировки
  const canGoPrevious = currentLessonIndex > 0 && 
    moduleLessons[currentLessonIndex - 1] && 
    !moduleLessons[currentLessonIndex - 1].locked;
  
  const canGoNext = currentLessonIndex < moduleLessons.length - 1 && 
    moduleLessons[currentLessonIndex + 1] && 
    !moduleLessons[currentLessonIndex + 1].locked;

  const moduleNum = module ? parseInt(module) : null;
  
  // Подготовка данных для отображения
  let title = "";
  let contentSections = []; // Массив для разделов Overview/Content
  let code = "";
  let assignmentSections = []; // Массив для разделов Assignment
  let resources = [];

  if (moduleNum && ((moduleNum >= 1 && moduleNum <= 4) || moduleNum === 6)) {
    // Модули 1-4 и 6: данные из таблицы lessons
    title = lessonData.title || "";
    
    // Content разделы
    if (lessonData.body) {
      contentSections.push({ title: "Overview", content: lessonData.body });
    }
    if (lessonData.questions) {
      contentSections.push({ title: "Questions", content: lessonData.questions });
    }
    
    // Code - для модулей 1-4 нет поля codeblock в таблице lessons
    
    // Assignment разделы
    if (lessonData.task) {
      assignmentSections.push({ title: "Assignment", content: lessonData.task });
    }
    if (lessonData.taskhard) {
      assignmentSections.push({ title: "Hard Assignment", content: lessonData.taskhard });
    }
    if (lessonData.datafile) {
      const dataFileUrl = lessonData.datafile;
      assignmentSections.push({
        title: "Data File",
        content: `${dataFileUrl}`
      });
    }
    
    // Resources
    if (lessonData.additional) {
      resources.push(...parseMarkdownLinks(lessonData.additional));
    }
    if (lessonData.comment) {
      resources.push(...parseMarkdownLinks(lessonData.comment));
    }
  } else if (moduleNum === 5) {
    // Модуль 5: данные из таблицы bugs
    title = lessonData.title || "";
    
    // Content разделы
    if (lessonData.hint) {
      contentSections.push({ title: "Hint", content: lessonData.hint });
    }
    if (lessonData.answer) {
      contentSections.push({ title: "Answer", content: lessonData.answer });
    }
    
    // Code
    code = lessonData.codeblock || "";
    
    // Assignment разделы
    if (lessonData.tags) {
      assignmentSections.push({ title: "Tags", content: lessonData.tags });
    }
    
    // Resources пустые для модуля 5
  }

  return (
    <div className="lesson-content">
      <div className="lesson-content-inner">
        {/* Lesson Title */}
        {title && (
          <div className="lesson-header">
            <h1 className="lesson-title">{title}</h1>
            <div className="lesson-divider" />
          </div>
        )}

        {/* Module 5: пример кода через markdown (как контент уроков на сайте) */}
        {moduleNum === 5 && code && (
          <section className="lesson-section">
            <div className="section-header">
              <div className="section-icon-wrapper">
                <div className="section-icon-dot section-icon-dot-green" />
              </div>
              <h2 className="section-title">Пример кода</h2>
            </div>
            <div className="lesson-content-text">
              <MarkdownContent content={wrapSolidityCodeblockMarkdown(code)} />
            </div>
          </section>
        )}

        {/* Content Sections (Overview/Questions/Hint/Answer) */}
        {contentSections.map((section, idx) => {
          const titleTranslations = {
            'Overview': 'Обзор',
            'Questions': 'Вопросы',
            'Hint': 'Подсказка',
            'Answer': 'Ответ'
          };
          const translatedTitle = titleTranslations[section.title] || section.title;
          const isSpoilerSection = moduleNum === 5 && (section.title === 'Hint' || section.title === 'Answer');
          const isHint = section.title === 'Hint';
          const isAnswer = section.title === 'Answer';
          const isExpanded = (isHint && spoilersOpen.hint) || (isAnswer && spoilersOpen.answer);

          return (
            <section key={idx} className="lesson-section">
              <div className="section-header">
                <BookOpen className="section-icon section-icon-primary" />
                <h2 className="section-title">{translatedTitle}</h2>
              </div>
              <div style={isSpoilerSection ? { position: 'relative' } : undefined}>
                <div className="lesson-content-text">
                  <MarkdownContent content={section.content} />
                </div>
                {isSpoilerSection && !isExpanded && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(22, 22, 22, 0.92)',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.55' numOctaves='4' stitchTiles='stitch'%3E%3Canimate attributeName='seed' from='0' to='100' dur='10.5s' repeatCount='indefinite'/%3E%3C/feTurbulence%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.7'/%3E%3C/svg%3E")`,
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}
                  />
                )}
              </div>
              {isSpoilerSection && (
                <div className="spoiler-toggle-wrap">
                  <Button
                    variant="ghost"
                    className="spoiler-toggle-btn"
                    onClick={() => setSpoilersOpen(prev => ({
                      ...prev,
                      ...(isHint && { hint: !prev.hint }),
                      ...(isAnswer && { answer: !prev.answer })
                    }))}
                  >
                    {isHint
                      ? (spoilersOpen.hint ? 'Скрыть подсказку' : 'Показать подсказку')
                      : (spoilersOpen.answer ? 'Скрыть ответ' : 'Показать ответ')}
                  </Button>
                </div>
              )}
            </section>
          );
        })}

        {/* Code Editor (modules 1-4 only; module 5 renders it above) */}
        {code && moduleNum !== 5 && (
          <section className="lesson-section">
            <div className="section-header">
              <div className="section-icon-wrapper">
                <div className="section-icon-dot section-icon-dot-green" />
              </div>
              <h2 className="section-title">Пример кода</h2>
            </div>
            <CodeEditor initialCode={code} highlightLang="solidity" />
          </section>
        )}

        {/* Assignment Sections */}
        {assignmentSections.map((section, idx) => {
          const titleTranslations = {
            'Assignment': 'Задание',
            'Hard Assignment': 'Сложное задание',
            'Data File': 'Файлы к уроку',
            'Tags': 'Теги'
          };
          const translatedTitle = titleTranslations[section.title] || section.title;
          
          return (
            <section key={idx} className="lesson-section">
              <div className="section-header">
                <Target className="section-icon section-icon-primary" />
                <h2 className="section-title">{translatedTitle}</h2>
              </div>
              <div className="assignment-box">
                <div className="assignment-text">
                  <MarkdownContent content={section.content} />
                </div>
              </div>
            </section>
          );
        })}

        {/* Resources */}
        {resources.length > 0 && (
          <section className="lesson-section">
            <div className="section-header">
              <Library className="section-icon section-icon-primary" />
              <h2 className="section-title">Ресурсы</h2>
            </div>
            <div className="resources-list">
              {resources.map((resource, idx) => (
                <a
                  key={idx}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="resource-link"
                >
                  <ExternalLink className="resource-icon" />
                  <span className="resource-title">{resource.title}</span>
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Navigation Buttons */}
        <div className="lesson-nav">
          <Button 
            variant="ghost" 
            className="lesson-nav-btn lesson-nav-btn-prev"
            onClick={handlePrevious}
            disabled={!canGoPrevious}
          >
            <ChevronLeft className="lesson-nav-icon" />
            Назад
          </Button>
          
          {isAuthenticated && lessonId !== "contents" && (
            <Button
              variant={isCompleted ? "outline" : "default"}
              className={`lesson-nav-btn lesson-nav-btn-complete ${isCompleted ? "lesson-nav-btn-complete-outline" : ""}`}
              onClick={isCompleted ? handleUnfinishLesson : handleFinishLesson}
            >
              {isCompleted ? (
                <>
                  <CheckCircle2 className="lesson-nav-icon" />
                  Отменить выполнение урока
                </>
              ) : (
                <>
                  <CheckCircle2 className="lesson-nav-icon" />
                  Урок выполнен
                </>
              )}
            </Button>
          )}
          
          <Button 
            className="lesson-nav-btn lesson-nav-btn-next"
            onClick={handleNext}
            disabled={!canGoNext}
          >
            Далее
            <ChevronRight className="lesson-nav-icon" />
          </Button>
        </div>
      </div>
    </div>
  );
};
